import type { KeyboardEvent, ReactElement } from 'react';
import type { ToggleProps } from './configTypes';

export function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  disabledReason,
}: ToggleProps): ReactElement {
  const tooltipText = disabled ? disabledReason || 'This feature is coming soon' : undefined;
  const disabledLabel = disabled ? (disabledReason ? '(Unavailable)' : '(Coming soon)') : null;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <div
      className={`flex items-center justify-between py-2 px-2 -mx-2 rounded-lg transition-colors ${
        disabled ? 'opacity-50' : 'hover:bg-theme-surface-hover/50 active:bg-theme-surface-hover'
      } group`}
      title={tooltipText}
      onClick={() => !disabled && onChange(!checked)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
    >
      <div className="flex-1 pr-3">
        <span
          className={`text-sm ${disabled ? 'text-theme-text-secondary' : 'text-white group-hover:text-blue-300'} transition`}
        >
          {label}
          {disabledLabel && (
            <span className="ml-2 text-xs text-theme-text-tertiary">{disabledLabel}</span>
          )}
        </span>
        {description && (
          <p className="text-xs text-theme-text-tertiary mt-0.5 group-hover:text-theme-text-secondary transition">
            {description}
          </p>
        )}
        {disabled && disabledReason && (
          <p className="text-xs text-theme-warning/80 mt-0.5">{disabledReason}</p>
        )}
      </div>
      <div
        className={`relative w-10 h-5 rounded-full transition-all shrink-0 ${
          disabled
            ? checked
              ? 'bg-theme-border cursor-not-allowed'
              : 'bg-theme-surface-hover cursor-not-allowed'
            : checked
              ? 'bg-theme-primary shadow-[0_0_8px_rgba(59,130,246,0.5)]'
              : 'bg-theme-border group-hover:bg-theme-text-tertiary'
        }`}
      >
        <div
          className={`absolute top-0.5 w-4 h-4 rounded-full shadow-md transition-all duration-200 ${
            disabled ? 'bg-theme-surface-hover' : 'bg-white'
          } ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
      </div>
    </div>
  );
}
